import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import useBreadcrumbs from '../../../utils/hooks/useBreadcrumbs';
import TitlePage from '../../../UI/TitlePage/TitlePage';
import Table from '../../../UI/Table/Table';
import Button from '../../../UI/Button/Button';
import Modal from '../../../UI/Modal/Modal';
import SurveyAPI from '../../../services/api/SurveyService';
import { PaginationType } from '../../../Types/APITypes';
import { NotifyContext } from '../../../context/notifyContext';

interface Question {
  id: number;
  question_text_fr: string;
  question_text_en: string;
  type: 'rating' | 'text' | 'select';
  display_order: number;
  is_active: boolean;
  is_required: boolean;
  min_rating?: number;
  max_rating?: number;
  max_length?: number;
  placeholder_fr?: string;
  placeholder_en?: string;
  options?: Array<{ value: string; label_fr: string; label_en: string }>;
}

const QuestionList: React.FC = () => {
  const navigate = useNavigate();
  const notify = useContext(NotifyContext);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationType>({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    has_more_pages: false,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);

  useBreadcrumbs([
    { label: 'Tableau de bord', is_link: true, href: '/app/dashboard' },
    { label: 'Gestion des sondages', is_link: true, href: '/app/surveys' },
    { label: 'Questions', is_link: false },
  ]);

  const fetchQuestions = useCallback(async (paginationData: PaginationType) => {
    try {
      setLoading(true);
      const response: any = await SurveyAPI.getAllQuestions({
        page: paginationData.current_page,
        per_page: paginationData.per_page,
        active: activeFilter,
      });

      if (response && response.data) {
        setQuestions(response.data.questions || []);
        setPagination(prev => ({
          ...prev,
          current_page: response.data.meta?.current_page || 1,
          total: response.data.meta?.total || 0,
          last_page: response.data.meta?.last_page || 1,
          has_more_pages: response.data.meta?.has_more_pages || false,
        }));
        setError(null);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des questions');
      notify(err.message || 'Erreur lors du chargement des questions', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    fetchQuestions(pagination);
  }, [pagination.current_page, pagination.per_page, activeFilter]);

  const handleDelete = async () => {
    if (!selectedQuestion) return;

    try {
      setActionLoading(true);
      await SurveyAPI.deleteQuestion(selectedQuestion.id);
      notify('Question supprimée avec succès', 'success');
      setShowDeleteModal(false);
      setSelectedQuestion(null);
      fetchQuestions(pagination);
    } catch (err: any) {
      notify(err.message || 'Erreur lors de la suppression', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (question: Question) => {
    try {
      setActionLoading(true);
      await SurveyAPI.toggleQuestionActive(question.id);
      notify('Statut mis à jour avec succès', 'success');
      fetchQuestions(pagination);
    } catch (err: any) {
      notify(err.message || 'Erreur lors de la mise à jour', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReorder = async (direction: 'up' | 'down', question: Question) => {
    const currentIndex = questions.findIndex(q => q.id === question.id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === questions.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const reorderedQuestions = [...questions];
    [reorderedQuestions[currentIndex], reorderedQuestions[newIndex]] = [
      reorderedQuestions[newIndex],
      reorderedQuestions[currentIndex],
    ];

    try {
      setActionLoading(true);
      const questionIds = reorderedQuestions.map(q => q.id);
      await SurveyAPI.reorderQuestions(questionIds);
      notify('Ordre mis à jour avec succès', 'success');
      fetchQuestions(pagination);
    } catch (err: any) {
      notify(err.message || 'Erreur lors du réordonnancement', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      Header: 'Ordre',
      accessor: 'display_order',
      Cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleReorder('up', row.original)}
            disabled={actionLoading || row.index === 0}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            ↑
          </button>
          <span>{row.original.display_order}</span>
          <button
            onClick={() => handleReorder('down', row.original)}
            disabled={actionLoading || row.index === questions.length - 1}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            ↓
          </button>
        </div>
      ),
    },
    {
      Header: 'Question (FR)',
      accessor: 'question_text_fr',
      Cell: ({ value }: any) => (
        <div className="max-w-md truncate" title={value}>
          {value}
        </div>
      ),
    },
    {
      Header: 'Question (EN)',
      accessor: 'question_text_en',
      Cell: ({ value }: any) => (
        <div className="max-w-md truncate" title={value}>
          {value}
        </div>
      ),
    },
    {
      Header: 'Type',
      accessor: 'type',
      Cell: ({ value }: any) => (
        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
          {value}
        </span>
      ),
    },
    {
      Header: 'Obligatoire',
      accessor: 'is_required',
      Cell: ({ value }: any) => (
        <span className={value ? 'text-green-600' : 'text-gray-400'}>
          {value ? 'Oui' : 'Non'}
        </span>
      ),
    },
    {
      Header: 'Actif',
      accessor: 'is_active',
      Cell: ({ row }: any) => (
        <button
          onClick={() => handleToggleActive(row.original)}
          disabled={actionLoading}
          className={`
            px-2 py-1 text-xs rounded-full transition-colors
            ${row.original.is_active
              ? 'bg-green-100 text-green-800 hover:bg-green-200'
              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }
            disabled:opacity-50
          `}
        >
          {row.original.is_active ? 'Actif' : 'Inactif'}
        </button>
      ),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/app/surveys/questions/${row.original.id}/edit`)}
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Modifier
          </button>
          <button
            onClick={() => {
              setSelectedQuestion(row.original);
              setShowDeleteModal(true);
            }}
            className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
          >
            Supprimer
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <TitlePage>
        <div className="flex justify-between items-center">
          <span>Gestion des Questions de Sondage</span>
          <Button
            onClick={() => navigate('/app/surveys/questions/create')}
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            Ajouter une question
          </Button>
        </div>
      </TitlePage>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Filtrer:</label>
          <button
            onClick={() => setActiveFilter(undefined)}
            className={`px-3 py-1 rounded ${activeFilter === undefined ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            Tous
          </button>
          <button
            onClick={() => setActiveFilter(true)}
            className={`px-3 py-1 rounded ${activeFilter === true ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            Actifs
          </button>
          <button
            onClick={() => setActiveFilter(false)}
            className={`px-3 py-1 rounded ${activeFilter === false ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            Inactifs
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow">
        <Table
          columns={columns as any}
          data={questions}
          fetchData={fetchQuestions}
          loading={loading}
          pageCount={pagination.last_page || 1}
          total={pagination.total || 0}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedQuestion(null);
        }}
        title="Confirmer la suppression"
      >
        <div className="p-4">
          <p className="mb-4">
            Êtes-vous sûr de vouloir supprimer cette question ? Cette action est irréversible.
          </p>
          {selectedQuestion && (
            <div className="bg-gray-50 p-3 rounded mb-4">
              <p className="font-medium">Question FR:</p>
              <p className="text-sm text-gray-600">{selectedQuestion.question_text_fr}</p>
              <p className="font-medium mt-2">Question EN:</p>
              <p className="text-sm text-gray-600">{selectedQuestion.question_text_en}</p>
            </div>
          )}
          <div className="flex justify-end gap-3">
            <Button
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedQuestion(null);
              }}
              className="bg-gray-200 hover:bg-gray-300"
            >
              Annuler
            </Button>
            <Button
              onClick={handleDelete}
              disabled={actionLoading}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {actionLoading ? 'Suppression...' : 'Supprimer'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default QuestionList;
